/**
 * Route <-> label mapping for the App Shell Left Nav. Router paths and
 * browser URLs match one-to-one because AppShell mounts `<BrowserRouter>`
 * without a basename.
 */

export type LeftNavPath =
  | "/search"
  | "/baseline"
  | "/validation"
  | "/launch"
  | "/stamper";

export type ValidationTabId =
  | "dashboard"
  | "step-breakdown"
  | "program-tickets"
  | "catalog-issues";

/** Default tab when the URL is missing or holds an unknown value. */
export const DEFAULT_VALIDATION_TAB_ID: ValidationTabId = "dashboard";

/** Narrow a raw URL param value to a known `ValidationTabId`. */
export function resolveValidationTabId(raw: string): ValidationTabId {
  switch (raw) {
    case "dashboard":
    case "step-breakdown":
    case "program-tickets":
    case "catalog-issues":
      return raw;
    default:
      return DEFAULT_VALIDATION_TAB_ID;
  }
}

/** Fallback for unknown or missing suffixes. */
export const DEFAULT_LEFT_NAV_PATH: LeftNavPath = "/search";

/** Order of this array is the rendered order in the Left Nav. */
export const LEFT_NAV_ITEMS: ReadonlyArray<{
  readonly path: LeftNavPath;
  readonly label: string;
}> = [
  { path: "/search", label: "Search" },
  { path: "/validation", label: "Validation" },
  { path: "/launch", label: "Launch" },
  { path: "/baseline", label: "Date Baseline" },
  { path: "/stamper", label: "Stamper Tool" },
];

/** Path -> label lookup, derived so the two never drift. */
export const LEFT_NAV_LABELS: Readonly<Record<LeftNavPath, string>> =
  LEFT_NAV_ITEMS.reduce((acc, item) => {
    acc[item.path] = item.label;
    return acc;
  }, {} as Record<LeftNavPath, string>);

const KNOWN_PATHS: ReadonlySet<string> = new Set(
  LEFT_NAV_ITEMS.map((item) => item.path)
);

/**
 * Match the first path segment of `suffix` against `LEFT_NAV_ITEMS`,
 * falling back to `DEFAULT_LEFT_NAV_PATH` for empty / `/` / unknown
 * suffixes. Handles bare (`"search"`), leading-slash (`"/search"`),
 * and long-form (`"/search/details/..."`) inputs uniformly.
 */
export function resolveLeftNavPath(suffix: string): LeftNavPath {
  const firstSegment = suffix.replace(/^\/+/, "").split("/")[0];
  const candidate = `/${firstSegment}`;
  if (KNOWN_PATHS.has(candidate)) {
    return candidate as LeftNavPath;
  }
  return DEFAULT_LEFT_NAV_PATH;
}

/**
 * Submission History lists the launch workflows a user submitted, so it
 * is only meaningful next to the Launch table. Legacy gates the drawer
 * on `tab === "launch"`; the shell equivalent is the `/launch` route.
 * Resolved through `resolveLeftNavPath` so a trailing slash or deeper
 * suffix still counts as the Launch page, matching the Left Nav
 * highlight.
 */
export const SUBMISSION_HISTORY_PATH: LeftNavPath = "/launch";

export function shouldShowSubmissionHistory(pathname: string): boolean {
  return resolveLeftNavPath(pathname) === SUBMISSION_HISTORY_PATH;
}
